import {SharedEmptyPicker} from '@github-ui/item-picker/SharedEmptyPicker'
import {AssigneeRepositoryPicker} from '@github-ui/item-picker/AssigneePicker'

import type {ProjectPickerProject$data as Project} from '@github-ui/item-picker/ProjectPickerProject.graphql'
import {AlertIcon, TableIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import {Suspense, useCallback, useEffect} from 'react'
import {MetadataLoading} from './MetadataLoading'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
// import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQueryLoader} from 'react-relay'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {MilestonesPickerGraphqlQuery, type Milestone, MilestonePickerBase} from '@github-ui/item-picker/MilestonePicker'
import type {MilestonePickerQuery} from '@github-ui/item-picker/MilestonePickerQuery.graphql'
import {LabelPickerGraphqlQuery, LabelPickerBase} from '@github-ui/item-picker/LabelPicker'
import type {LabelPickerQuery} from '@github-ui/item-picker/LabelPickerQuery.graphql'
import {ProjectPickerGraphqlQuery, ProjectPicker} from '@github-ui/item-picker/ProjectPicker'
import type {ProjectPickerQuery} from '@github-ui/item-picker/ProjectPickerQuery.graphql'
import {IssueTypePickerGraphqlQuery, type IssueType, IssueTypePickerBase} from '@github-ui/item-picker/IssueTypePicker'
import type {IssueTypePickerQuery} from '@github-ui/item-picker/IssueTypePickerQuery.graphql'

export const MetadataSelectors = () => {
  const {optionConfig} = useIssueCreateConfigContext()
  const {
    repository,
    labels,
    setLabels,
    assignees,
    setAssignees,
    projects,
    setProjects,
    milestone,
    setMilestone,
    issueType,
    setIssueType,
  } = useIssueCreateDataContext()

  const canAssign = repository?.viewerIssueCreationPermissions?.assignable
  const canLabel = repository?.viewerIssueCreationPermissions?.labelable
  const canMilestone = repository?.viewerIssueCreationPermissions?.milestoneable
  const canProject = repository?.viewerIssueCreationPermissions?.triageable
  const canIssueType =
    repository?.viewerIssueCreationPermissions?.typeable && repository?.viewerIssueCreationPermissions?.triageable

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
    shortcutEnabled: optionConfig.singleKeyShortcutsEnabled,
    shortcutHintVisible: false,
  }

  const [labelsRef, loadLabels, disposeLabels] = useQueryLoader<LabelPickerQuery>(LabelPickerGraphqlQuery)
  const [projectsRef, loadProjects, disposeProjects] = useQueryLoader<ProjectPickerQuery>(ProjectPickerGraphqlQuery)
  const [milestonesRef, loadMilestones, disposeMilestones] =
    useQueryLoader<MilestonePickerQuery>(MilestonesPickerGraphqlQuery)
  const [issueTypesRef, loadIssueTypes, disposeIssueTypes] =
    useQueryLoader<IssueTypePickerQuery>(IssueTypePickerGraphqlQuery)

  useEffect(() => {
    if (repository) {
      loadLabels({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadProjects({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadMilestones({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      if (repository?.viewerIssueCreationPermissions?.typeable)
        loadIssueTypes({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
    }
    return () => {
      disposeLabels()
      disposeProjects()
      disposeMilestones()
      disposeIssueTypes()
    }
  }, [
    repository,
    disposeLabels,
    disposeMilestones,
    loadLabels,
    loadMilestones,
    disposeProjects,
    loadProjects,
    disposeIssueTypes,
    loadIssueTypes,
  ])

  const setMilestoneCallback = useCallback(
    (milestones: Milestone[] | null) => {
      setMilestone(milestones?.[0] || null)
    },
    [setMilestone],
  )

  const setIssueTypeCallback = useCallback(
    (issueTypes: IssueType[] | null) => {
      setIssueType(issueTypes?.[0] || null)
    },
    [setIssueType],
  )

  return (
    <Suspense fallback={<MetadataLoading />}>
      <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap'}}>
        {repository && (canAssign || assignees.length > 0) && (
          <AssigneeRepositoryPicker
            repoName={repository.name}
            repoOwner={repository.owner.login}
            readonly={!canAssign}
            assignees={assignees}
            participants={[]}
            onSelectionChange={setAssignees}
            {...sharedConfigProps}
          />
        )}
        {labelsRef && (canLabel || labels.length > 0) && (
          <LabelPickerBase
            readonly={!canLabel}
            labels={labels}
            labelQueryRef={labelsRef}
            onSelectionChange={setLabels}
            {...sharedConfigProps}
          />
        )}
        {projectsRef && repository && !optionConfig.scopedProjectTitle && (canProject || projects.length > 0) && (
          <ErrorBoundary
            fallback={<SharedEmptyPicker label={'Projects are unavailable'} leadingIcon={AlertIcon} readonly />}
          >
            <ProjectPicker
              pickerId="create-issue-projects-picker"
              projectQueryRef={projectsRef}
              readonly={!canProject}
              onSave={(selectedProjects: Project[]) => {
                setProjects(selectedProjects)
              }}
              selectedProjects={projects}
              firstSelectedProjectTitle={projects.length > 0 ? projects[0]!.title : ''}
              owner={repository.owner.login}
              repo={repository.name}
              {...sharedConfigProps}
            />
          </ErrorBoundary>
        )}
        {projectsRef && optionConfig.scopedProjectTitle && (
          <SharedEmptyPicker label={optionConfig.scopedProjectTitle} leadingIcon={TableIcon} readonly />
        )}
        {milestonesRef && (canMilestone || milestone !== null) && (
          <MilestonePickerBase
            readonly={!canMilestone}
            activeMilestone={milestone}
            milestoneQueryRef={milestonesRef}
            onSelectionChange={setMilestoneCallback}
            {...sharedConfigProps}
          />
        )}
        {issueTypesRef && canIssueType && (
          <IssueTypePickerBase
            readonly={false}
            activeIssueType={issueType}
            issueTypeQueryRef={issueTypesRef}
            onSelectionChange={setIssueTypeCallback}
            {...sharedConfigProps}
          />
        )}
      </Box>
    </Suspense>
  )
}

try{ MetadataSelectors.displayName ||= 'MetadataSelectors' } catch {}